import { useState, useEffect, useCallback, useRef } from 'react';
import './CustomerGraph.css';

interface CustomerGraphProps {
  apiBaseUrl: string;
}

const STYLE_PILLS = ['Classic', 'Minimalist', 'Neutral tones', 'Prefers silk'];
const SIZE_TEXT = 'Size 6 · Petite';
const BROWSING_SIGNALS = [
  { label: 'Viewed', value: 'Cashmere Sweaters (3×)' },
  { label: 'Searched', value: 'Fall workwear' },
];
const PRODUCTS = [
  { name: 'Cashmere V-Neck Sweater', color: '#8B7355' },
  { name: 'Wool Blazer', color: '#4A4A5A' },
  { name: 'Silk Blouse', color: '#C4A882' },
];
const INSIGHT_FULL = 'Likely looking for: elevated fall work wardrobe';

interface AnimState {
  pillCount: number;
  showSize: boolean;
  browsingCount: number;
  purchaseCount: number;
  insightText: string;
  showCursor: boolean;
  fadeOut: boolean;
}

const INITIAL: AnimState = {
  pillCount: 0,
  showSize: false,
  browsingCount: 0,
  purchaseCount: 0,
  insightText: '',
  showCursor: false,
  fadeOut: false,
};

export function CustomerGraph(_props: CustomerGraphProps) {
  const [s, setS] = useState<AnimState>(INITIAL);
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>(resolve => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setS(INITIAL);
      await sleep(600);

      // Style pills — stagger in one by one
      for (let i = 1; i <= STYLE_PILLS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, pillCount: i }));
        await sleep(300);
      }
      await sleep(400);

      // Size
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showSize: true }));
      await sleep(500);

      // Browsing signals
      for (let i = 1; i <= BROWSING_SIGNALS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, browsingCount: i }));
        await sleep(400);
      }
      await sleep(400);

      // Recent purchases
      for (let i = 1; i <= PRODUCTS.length; i++) {
        if (!loopRef.current) return;
        setS(prev => ({ ...prev, purchaseCount: i }));
        await sleep(350);
      }
      await sleep(400);

      // AI insight — typewriter
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, showCursor: true }));
      for (let i = 1; i <= INSIGHT_FULL.length; i++) {
        if (!loopRef.current) return;
        const text = INSIGHT_FULL.slice(0, i);
        setS(prev => ({ ...prev, insightText: text }));
        await sleep(35);
      }
      await sleep(200);
      setS(prev => ({ ...prev, showCursor: false }));

      // Hold
      await sleep(3000);

      // Fade out
      if (!loopRef.current) return;
      setS(prev => ({ ...prev, fadeOut: true }));
      await sleep(900);

      await sleep(500);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  return (
    <div className="customer-graph">
      <div className={`cg-card sc-glass${s.fadeOut ? ' cg-fade-out' : ''}`}>

        {/* ── Style Preferences ── */}
        <div className={`cg-section${s.pillCount > 0 ? ' cg-visible' : ''}`}>
          <div className="sc-section-label">Style Preferences</div>
          <div className="cg-pills">
            {STYLE_PILLS.map((pill, i) => (
              <span
                key={pill}
                className={`sc-pill cg-pill${i < s.pillCount ? ' cg-pill-in' : ''}`}
              >
                {pill}
              </span>
            ))}
          </div>
        </div>

        {/* ── Size ── */}
        <div className={`cg-section${s.showSize ? ' cg-visible' : ''}`}>
          <div className="sc-section-label">Size</div>
          <div className={`cg-size-badge${s.showSize ? ' cg-slide-in' : ''}`}>
            {SIZE_TEXT}
          </div>
        </div>

        {/* ── Browsing Activity ── */}
        <div className={`cg-section${s.browsingCount > 0 ? ' cg-visible' : ''}`}>
          <div className="sc-section-label">Browsing Activity</div>
          <div className="cg-browsing-list">
            {BROWSING_SIGNALS.map((sig, i) => (
              <div
                key={sig.label}
                className={`cg-browsing-row${i < s.browsingCount ? ' cg-row-in' : ''}`}
              >
                <span className="cg-browsing-label">{sig.label}:</span>
                <span className="cg-browsing-value">{sig.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Recent Purchases ── */}
        <div className={`cg-section${s.purchaseCount > 0 ? ' cg-visible' : ''}`}>
          <div className="sc-section-label">Recent Purchases</div>
          <div className="cg-purchases">
            {PRODUCTS.map((p, i) => (
              <div
                key={p.name}
                className={`cg-product-card${i < s.purchaseCount ? ' cg-product-in' : ''}`}
              >
                <div className="cg-product-thumb" style={{ background: p.color }} />
                <div className="cg-product-name">{p.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── AI Insight ── */}
        <div className={`cg-section${s.insightText || s.showCursor ? ' cg-visible' : ''}`}>
          <div className="sc-section-label">AI Insight</div>
          <div className="cg-insight">
            <span className="cg-insight-icon">✦</span>
            <span className="cg-insight-text">
              {s.insightText}
              {s.showCursor && <span className="cg-cursor" />}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
